import { useState } from "react";
import { type GuidanceVideo, isSafeHttpUrl } from "@keydials/api-client";

/**
 * Author-provided guidance videos for a node. Shows one video at a time — the default first (or the first
 * in order) — with a selector to switch when there's more than one. Playback is driven entirely by the
 * server-resolved fields: an `embedKind` of "youtube"/"vimeo" plays in a provider iframe (the URL is a
 * normalised, server-validated embed URL), anything else plays as a native file. Videos without a
 * playable URL (e.g. an upload still resolving) are skipped. Renders nothing when there are no videos.
 */
export function GuidanceVideos({ videos }: { videos?: GuidanceVideo[] | null }) {
  const list = (videos ?? []).filter((v) => !!v.playbackUrl);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  if (list.length === 0) {
    return null;
  }

  const fallback = list.find((v) => v.isDefault) ?? list[0];
  const active = list.find((v) => v.key === activeKey) ?? fallback;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <VideoPlayer video={active} />
      {list.length > 1 && (
        <div role="tablist" aria-label="Videos" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {list.map((v) => {
            const on = v.key === active.key;
            return (
              <button
                key={v.key}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setActiveKey(v.key)}
                title={v.title}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  maxWidth: 240,
                  padding: "5px 10px",
                  fontSize: 12.5,
                  fontWeight: on ? 600 : 500,
                  cursor: "pointer",
                  borderRadius: "var(--radius-sm)",
                  border: `var(--border-w) solid ${on ? "var(--accent-border)" : "var(--border)"}`,
                  background: on ? "var(--accent-soft)" : "var(--surface-2)",
                  color: on ? "var(--accent-text)" : "var(--text-muted)",
                }}
              >
                <PlayGlyph />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.title}</span>
                {v.isDefault && <span style={{ fontSize: 10.5, opacity: 0.65 }}>· default</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Whatever a playback surface needs to pick and configure itself — a subset of {@link GuidanceVideo} that
 * any file-backed video source (guidance video, or a Video node-attribute's resolved value) can supply.
 */
export interface VideoPlaybackSource {
  title: string;
  embedKind?: "youtube" | "vimeo" | "file" | null;
  playbackUrl?: string | null;
  /** "Audio" plays in a native `<audio>` at its own height (KEY-565); anything else is a video. */
  mediaKind?: "Video" | "Audio" | null;
}

/**
 * Renders one playable video: a sandboxed provider iframe (YouTube/Vimeo) or a native `<video>` for a direct
 * file/SAS URL — or, for an audio row (KEY-565), a native `<audio>`. Reused wherever a resolved video source
 * needs playback — guidance videos, and the Video
 * node-attribute type. `onPlaybackError` fires only for the native-file path — e.g. to null out a SAS URL
 * that has expired since it was resolved (the provider iframe has no comparable failure signal).
 */
export function VideoPlayer({ video, onPlaybackError }: { video: VideoPlaybackSource; onPlaybackError?: () => void }) {
  const url = video.playbackUrl ?? "";
  if (video.mediaKind === "Audio") {
    // KEY-565: a recording gets the browser's own audio controls at their natural height — no 16:9 black box
    // around a control bar, and never a provider iframe, since the server doesn't provider-detect audio.
    return (
      <audio
        key={url}
        src={url}
        controls
        preload="metadata"
        title={video.title}
        onError={onPlaybackError}
        style={{ display: "block", width: "100%" }}
      />
    );
  }
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        background: "#000",
        borderRadius: "var(--radius)",
        overflow: "hidden",
        border: "var(--border-w) solid var(--border)",
      }}
    >
      {video.embedKind === "file" ? (
        // Direct file / SAS blob — native playback; no third-party scripts.
        <video
          key={url}
          src={url}
          controls
          playsInline
          onError={onPlaybackError}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "#000" }}
        />
      ) : (
        // Provider embed (YouTube/Vimeo). The URL is a server-normalised, allow-listed embed URL; the
        // sandbox limits the frame to its own (provider) origin so it can never touch this page.
        <iframe
          key={url}
          src={url}
          title={video.title}
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
        />
      )}
    </div>
  );
}

/** How a classified URL should be embedded, or the direct-file fallback when it names no known provider. */
export type VideoEmbedKind = "youtube" | "vimeo" | "file";

export interface ClassifiedVideoUrl {
  embedKind: VideoEmbedKind;
  playbackUrl: string;
}

// youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, youtube.com/shorts/ID — 11-char id.
const YOUTUBE_RE = /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;
// vimeo.com/ID or player.vimeo.com/video/ID — numeric id.
const VIMEO_RE = /vimeo\.com\/(?:video\/)?(\d+)/i;

/**
 * Classifies a pasted video URL for playback — YouTube/Vimeo detection plus a direct-file fallback,
 * mirroring the server's `GuidanceVideoMapping.ResolveExternal` (guidance videos get this resolution from
 * the server; a Video node-attribute's link value doesn't, so the viewer has to do it itself). Returns null
 * for anything that isn't a safe http(s) URL — unlike the server mapping, which never sees an unsafe URL in
 * the first place, this runs against fields callers may not have validated.
 */
export function classifyVideoUrl(url: string | null | undefined): ClassifiedVideoUrl | null {
  if (!url || !isSafeHttpUrl(url)) {
    return null;
  }
  const youTube = YOUTUBE_RE.exec(url);
  if (youTube) {
    return { embedKind: "youtube", playbackUrl: `https://www.youtube-nocookie.com/embed/${youTube[1]}` };
  }
  const vimeo = VIMEO_RE.exec(url);
  if (vimeo) {
    return { embedKind: "vimeo", playbackUrl: `https://player.vimeo.com/video/${vimeo[1]}` };
  }
  return { embedKind: "file", playbackUrl: url };
}

function PlayGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M2 1.5 L8.5 5 L2 8.5 Z" fill="currentColor" />
    </svg>
  );
}
